import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { Button, Text } from '@tremor/react'
import { XMarkIcon, SparklesIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { sanitizeUserInput } from '../../utils/security'

function GoalAIGenerationModal({ isOpen, onClose, onSubmit, loading = false }) {
    const { t, i18n } = useTranslation()
    const [currentStep, setCurrentStep] = useState(0) // Start at 0 for scope selection
    const [formResponses, setFormResponses] = useState({})
    const [error, setError] = useState(null)
    const [scope, setScope] = useState(null) // Start with null, user must select
    const [estimatedCredits, setEstimatedCredits] = useState(null)

    // Academic goal questions based on research methodology framework
    const academicQuestions = {
        en: [
            "What exactly is the phenomenon you intend to study and what scientific gap exists in the literature?",
            "Who or what will be studied - what is your unit of analysis (individuals, brands, organizations, content, platforms)?",
            "In what context does the phenomenon occur - is there temporal or geographical delimitation?",
            "What central concepts will be analyzed and what are the main variables?",
            "Do you intend to describe, explain, compare or predict something with this study?",
            "What is the scientific and social relevance of the problem you're investigating?",
            "Can your objective be empirically investigated and are accessible data available?",
            "What methods will allow you to achieve the objective and is it measurable or qualitatively analyzable?",
            "Can the objective be achieved within available time and resources?",
            "Does your objective clearly indicate what will be analyzed, in whom/what, in what context, and for what scientific purpose?"
        ],
        es: [
            "¿Cuál es exactamente el fenómeno que pretendes estudiar y qué laguna científica existe en la literatura?",
            "¿Quién o qué será estudiado - cuál es tu unidad de análisis (individuos, marcas, organizaciones, contenidos, plataformas)?",
            "¿En qué contexto ocurre el fenómeno - existe delimitación temporal o geográfica?",
            "¿Qué conceptos centrales serán analizados y cuáles son las variables principales?",
            "¿Pretendes describir, explicar, comparar o predecir algo con este estudio?",
            "¿Cuál es la relevancia científica y social del problema que investigas?",
            "¿Puede tu objetivo ser empíricamente investigado y existen datos accesibles disponibles?",
            "¿Qué métodos permitirán lograr el objetivo y es mensurable o analizable cualitativamente?",
            "¿Puede el objetivo ser alcanzado dentro del tiempo y recursos disponibles?",
            "¿Tu objetivo indica claramente qué será analizado, en quién/qué, en qué contexto, y con qué finalidad científica?"
        ],
        pt: [
            "Qual é exatamente o fenómeno que pretende estudar e que lacuna científica existe na literatura?",
            "Quem ou o que será estudado - qual é a sua unidade de análise (indivíduos, marcas, organizações, conteúdos, plataformas)?",
            "Em que contexto ocorre o fenómeno - existe delimitação temporal ou geográfica?",
            "Que conceitos centrais serão analisados e quais são as variáveis principais?",
            "Pretende descrever, explicar, comparar ou prever algo com este estudo?",
            "Qual é a relevância científica e social do problema que investiga?",
            "Pode o seu objetivo ser empiricamente investigado e existem dados acessíveis disponíveis?",
            "Que métodos permitirão atingir o objetivo e é mensurável ou analisável qualitativamente?",
            "Pode o objetivo ser alcançado dentro do tempo e recursos disponíveis?",
            "O seu objetivo indica claramente o que será analisado, em quem/quê, em que contexto, e com que finalidade científica?"
        ]
    }

    // Business goal questions for consumer behavior research
    const businessQuestions = {
        en: [
            "What consumer behavior do you intend to understand or explain?",
            "What consumer decision is at stake (attention, choice, preference, purchase, loyalty)?",
            "What stage of the consumer journey will be analyzed (awareness, consideration, decision, post-purchase)?",
            "What data exists but is not interpreted behaviorally in your organization?",
            "What marketing decisions are currently based on assumptions that need validation?",
            "What emotional or cognitive factors influence the consumer decision?",
            "What communication stimuli generate greater engagement with your target audience?",
            "What psychological barriers prevent consumer action in your context?",
            "What impact will this have on the consumer experience and business outcomes?",
            "What actionable insight do you expect to generate from this behavioral understanding?"
        ],
        es: [
            "¿Qué comportamiento del consumidor pretendes comprender o explicar?",
            "¿Qué decisión del consumidor está en juego (atención, elección, preferencia, compra, fidelización)?",
            "¿Qué etapa del viaje del consumidor será analizada (awareness, consideración, decisión, post-compra)?",
            "¿Qué datos existen pero no están interpretados comportamentalmente en tu organización?",
            "¿Qué decisiones de marketing están actualmente basadas en suposiciones que necesitan validación?",
            "¿Qué factores emocionales o cognitivos influencian la decisión del consumidor?",
            "¿Qué estímulos comunicacionales generan mayor engagement con tu audiencia objetivo?",
            "¿Qué barreras psicológicas impiden la acción del consumidor en tu contexto?",
            "¿Qué impacto tendrá esto en la experiencia del consumidor y resultados del negocio?",
            "¿Qué insight accionable esperas generar de esta comprensión comportamental?"
        ],
        pt: [
            "Que comportamento do consumidor pretende compreender ou explicar?",
            "Que decisão do consumidor está em causa (atenção, escolha, preferência, compra, fidelização)?",
            "Que etapa da jornada do consumidor será analisada (awareness, consideração, decisão, pós-compra)?",
            "Que dados existem mas não estão interpretados comportamentalmente na sua organização?",
            "Que decisões de marketing estão atualmente baseadas em suposições que precisam validação?",
            "Que fatores emocionais ou cognitivos influenciam a decisão do consumidor?",
            "Que estímulos comunicacionais geram maior envolvimento com o seu público-alvo?",
            "Que barreiras psicológicas impedem a ação do consumidor no seu contexto?",
            "Que impacto terá isto na experiência do consumidor e resultados do negócio?",
            "Que insight acionável espera gerar desta compreensão comportamental?"
        ]
    }

    // Scope selection question
    const scopeQuestion = {
        en: "What type of research are you conducting?",
        es: "¿Qué tipo de investigación estás realizando?",
        pt: "Que tipo de investigação está a realizar?"
    }
    
    const scopeOptions = {
        en: {
            academic: "Academic Research",
            business: "Business Research"
        },
        es: {
            academic: "Investigación Académica",
            business: "Investigación Empresarial"
        },
        pt: {
            academic: "Investigação Académica",
            business: "Investigação Empresarial"
        }
    }
    
    const questions = scope === 'academic' ? academicQuestions : businessQuestions
    const currentQuestions = questions[i18n.language] || questions.en

    // Check if we can generate (scope selected + at least 3 responses with 10+ chars each)
    const answeredQuestions = Object.keys(formResponses).filter(step => 
        step !== '0' && formResponses[step] && formResponses[step].trim().length >= 10
    ).length
    const canGenerate = scope && answeredQuestions >= 3

    // Estimate credits needed
    const estimateCreditsForGeneration = () => {
        const responses = Object.values(formResponses).filter(response => 
            response && response.trim().length >= 10
        )
        const totalLength = responses.reduce((sum, response) => sum + response.length, 0)
        
        // Credit calculation:
        // - Base cost: 1 credit
        // - Content-based: 1 credit per ~300 characters
        // - Generate 3-5 suggestions: multiply by 1.8
        const baseCredits = 1
        const contentCredits = Math.ceil(totalLength / 300) * 0.4
        const totalEstimate = Math.ceil((baseCredits + contentCredits) * 1.8)
        
        return Math.max(2, Math.min(8, totalEstimate))
    }

    // Update credit estimate
    React.useEffect(() => {
        if (canGenerate) {
            setEstimatedCredits(estimateCreditsForGeneration())
        } else {
            setEstimatedCredits(null)
        }
    }, [canGenerate, formResponses])

    const handleInputChange = (value) => {
        if (currentStep === 0) {
            // Scope selection
            setScope(value)
        } else {
            setFormResponses(prev => ({
                ...prev,
                [currentStep]: value
            }))
        }
    }

    const handleNext = () => {
        // Step 0: Scope selection (required)
        if (currentStep === 0) {
            if (!scope) {
                setError(t('Research.ObjectivosHipoteses.Goals.Form.RequiredField'))
                return
            }
            setError(null)
            setCurrentStep(1)
            return
        }
        
        // Steps 1-3: Required responses
        const currentResponse = formResponses[currentStep]
        if (currentStep <= 3 && (!currentResponse || currentResponse.trim().length < 10)) {
            setError(t('Research.ObjectivosHipoteses.Goals.Form.ResponseTooShort'))
            return
        }
        
        setError(null)
        if (currentStep < 10) {
            setCurrentStep(currentStep + 1)
        }
    }

    const handlePrev = () => {
        setError(null)
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
        }
    }

    const handleSkip = () => {
        setError(null)
        if (currentStep > 3 && currentStep < 10) { // Can only skip after first 3 questions
            setCurrentStep(currentStep + 1)
        }
    }

    const handleSubmit = async () => {
        // Only include substantial responses
        const responses = Object.keys(formResponses)
            .filter(step => formResponses[step] && formResponses[step].trim().length >= 10)
            .map(step => ({
                question_number: parseInt(step),
                question_text: currentQuestions[parseInt(step) - 1],
                user_response: sanitizeUserInput(formResponses[step])
            }))

        if (responses.length < 3) {
            setError(t('Research.ObjectivosHipoteses.Goals.Form.MinimumResponses'))
            return
        }

        try {
            setError(null)
            await onSubmit(responses, scope)
            handleClose()
        } catch (err) {
            setError(err.message || t('Research.ObjectivosHipoteses.Goals.Form.SubmitError'))
        }
    }

    const handleClose = () => {
        if (!loading) {
            setCurrentStep(0)
            setFormResponses({})
            setError(null)
            setScope(null)
            setEstimatedCredits(null)
            onClose()
        }
    }


    return (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />
            <div className="fixed inset-0">
                <Dialog.Panel className="w-full h-full bg-white flex flex-col">
                    <div className="flex items-center justify-between p-6 border-b">
                        <div>
                            <Dialog.Title className="text-lg font-semibold text-gray-900">
                                {t('Research.ObjectivosHipoteses.Goals.Form.Title')}
                            </Dialog.Title>
                            <Text className="text-gray-600 mt-1">
                                {t('Research.ObjectivosHipoteses.Goals.Form.Description')}
                            </Text>
                        </div>
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Main scrollable content */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="p-8 max-w-4xl mx-auto h-full flex flex-col">
                            <div className="flex-1 flex flex-col justify-center">
                                <div className="mb-12">
                                    {currentStep === 0 ? (
                                        // Scope selection step
                                        <>
                                            <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-8 leading-tight text-left">
                                                {scopeQuestion[i18n.language] || scopeQuestion.en}
                                            </h2>
                                            
                                            <div className="space-y-4">
                                                <button
                                                    onClick={() => handleInputChange('academic')}
                                                    disabled={loading}
                                                    className={`w-full p-6 text-left rounded-lg border-2 transition-all ${
                                                        scope === 'academic' 
                                                            ? 'border-iavisus-100 bg-iavisus-50 text-iavisus-900'
                                                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                                    }`}
                                                >
                                                    <div className="font-medium text-lg">
                                                        {scopeOptions[i18n.language]?.academic || scopeOptions.en.academic}
                                                    </div>
                                                    <div className="text-sm text-gray-500 mt-1">
                                                        {t('Research.ObjectivosHipoteses.Scopes.AcademicDescription')}
                                                    </div>
                                                </button>
                                                
                                                <button
                                                    onClick={() => handleInputChange('business')}
                                                    disabled={loading}
                                                    className={`w-full p-6 text-left rounded-lg border-2 transition-all ${
                                                        scope === 'business' 
                                                            ? 'border-iavisus-100 bg-iavisus-50 text-iavisus-900'
                                                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                                    }`}
                                                >
                                                    <div className="font-medium text-lg">
                                                        {scopeOptions[i18n.language]?.business || scopeOptions.en.business}
                                                    </div>
                                                    <div className="text-sm text-gray-500 mt-1">
                                                        {t('Research.ObjectivosHipoteses.Scopes.BusinessDescription')}
                                                    </div>
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        // Regular question steps
                                        <>
                                            <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-8 leading-tight text-left">
                                                {currentQuestions[currentStep - 1]}
                                            </h2>
                                            
                                            <div>
                                                <textarea
                                                    rows={1}
                                                    value={formResponses[currentStep] || ''}
                                                    onChange={(e) => handleInputChange(e.target.value)}
                                                    placeholder={t('Research.ObjectivosHipoteses.Goals.Form.ResponsePlaceholder')}
                                                    className="w-full text-lg p-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-iavisus-100 focus:outline-none focus:ring-0 transition-colors resize-none overflow-hidden"
                                                    maxLength={500}
                                                    disabled={loading}
                                                    style={{
                                                        minHeight: 'auto',
                                                        lineHeight: '1.5'
                                                    }}
                                                    onInput={(e) => {
                                                        e.target.style.height = 'auto';
                                                        e.target.style.height = e.target.scrollHeight + 'px';
                                                    }}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            
                            {/* Bottom section with error and buttons */}
                            <div className="border-t border-gray-100 pt-6 mt-auto">
                                {error && (
                                    <div className="mb-4 text-center">
                                        <Text className="text-red-600 text-sm">{error}</Text>
                                    </div>
                                )}

                                <div className="flex justify-between items-center">
                                    <Button
                                        variant="secondary"
                                        onClick={handlePrev}
                                        disabled={currentStep === 0 || loading}
                                        icon={ChevronLeftIcon}
                                        className="bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                                    >
                                        {t('Common.Previous')}
                                    </Button>

                                    <div className="flex space-x-3">
                                        {currentStep > 3 && currentStep !== 0 && (
                                            <Button
                                                variant="secondary"
                                                onClick={handleSkip}
                                                disabled={loading}
                                                className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                                            >
                                                {t('Common.Skip')}
                                            </Button>
                                        )}

                                        {currentStep < 10 ? (
                                            <Button
                                                onClick={handleNext}
                                                disabled={loading || (currentStep === 0 && !scope)}
                                                icon={ChevronRightIcon}
                                                iconPosition="right"
                                                className="bg-iavisus-100 text-white hover:bg-iavisus-200 disabled:opacity-50"
                                            >
                                                {t('Common.Next')}
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={handleSubmit}
                                                icon={SparklesIcon}
                                                disabled={!canGenerate || loading}
                                                className="bg-iavisus-100 text-white hover:bg-iavisus-200 disabled:opacity-50"
                                            >
                                                {loading 
                                                    ? t('Common.Generating') 
                                                    : estimatedCredits 
                                                        ? `${t('Research.ObjectivosHipoteses.Goals.Form.GenerateGoals')} (${estimatedCredits} ${t('Common.Credits')})`
                                                        : t('Research.ObjectivosHipoteses.Goals.Form.GenerateGoals')
                                                }
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    )
}

export default GoalAIGenerationModal